
import React, { useState, useEffect } from 'react';
import type { User, Chef } from '../types';
import ChefHatIcon from './icons/ChefHatIcon';
import { getTranslation } from '../utils/translations';
import { getChefDisplayName, isUserNicknameReservedByChef } from '../utils/chefDisplay';
import { isNutritionConsultant } from '../utils/userType';

import CopyButton from './CopyButton';
import ProfileHeader from './ProfileHeader';
import { resolveUserProfileBadges } from '../utils/userProfileBadges';
import { normalizeAvatarUrl } from '../utils/avatarUrl';
import { resolveMediaUrlForSave, isStoredMediaUrl } from '../services/mediaService';

interface PersonalDetailsModalProps {
    isOpen: boolean;
    onClose: () => void;
    user: User;
    chefs?: Chef[];
    onSave: (details: Partial<User>) => Promise<void>;
    onRequestChefRecognition?: () => void;
    onViewChef?: (chefId: string) => void;
    guidedMode?: boolean;
    onGoPro?: () => void;
    onEmailChange?: (email: string) => Promise<{ accessUrl?: string }>;
}

const PersonalDetailsModal: React.FC<PersonalDetailsModalProps> = ({
    isOpen,
    onClose,
    user,
    chefs = [],
    onSave,
    onRequestChefRecognition,
    onViewChef,
    guidedMode = false,
    onGoPro,
    onEmailChange,
}) => {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [nickname, setNickname] = useState('');
    const [birthday, setBirthday] = useState('');
    const [email, setEmail] = useState('');
    const [profileBio, setProfileBio] = useState('');
    const [consultantCertification, setConsultantCertification] = useState('');
    const [avatarUrl, setAvatarUrl] = useState('');
    const [coverPhotoUrl, setCoverPhotoUrl] = useState('');
    const [isEditingAvatar, setIsEditingAvatar] = useState(false);
    const [isEditingCover, setIsEditingCover] = useState(false);
    const [newAccessUrl, setNewAccessUrl] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [showReservedNicknameModal, setShowReservedNicknameModal] = useState(false);

    const t = (key: Parameters<typeof getTranslation>[1]) => getTranslation(user.language, key);
    const linkedChef = user.chefId ? chefs.find((c) => c.id === user.chefId) : null;
    const isConsultant = isNutritionConsultant(user);

    useEffect(() => {
        if (isOpen && user) {
            setFirstName(user.firstName || '');
            setLastName(user.lastName || '');
            setNickname(user.nickname || '');
            setEmail(user.email || '');
            setBirthday(user.birthday || '');
            setProfileBio(user.profileBio || '');
            setConsultantCertification(user.consultantCertification || '');
            const initialAvatar = normalizeAvatarUrl(user.avatarUrl) || '';
            const initialCover = normalizeAvatarUrl(user.coverPhotoUrl) || '';
            setAvatarUrl(isStoredMediaUrl(initialAvatar) ? '' : initialAvatar);
            setCoverPhotoUrl(isStoredMediaUrl(initialCover) ? '' : initialCover);
            setIsEditingAvatar(false);
            setIsEditingCover(false);
            setNewAccessUrl('');
            setError('');
            setShowReservedNicknameModal(false);
        }
    }, [isOpen, user]);

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        if (isUserNicknameReservedByChef(chefs, nickname, user.uid, user.chefId)) {
            setShowReservedNicknameModal(true);
            return;
        }
        setIsLoading(true);
        try {
            const [avatarStored, coverStored] = await Promise.all([
                resolveMediaUrlForSave('user_avatar', avatarUrl, user.avatarUrl),
                resolveMediaUrlForSave('user_cover', coverPhotoUrl, user.coverPhotoUrl),
            ]);

            const profileUpdates: Partial<User> = {
                firstName,
                lastName,
                nickname,
                birthday,
                profileBio: profileBio.trim(),
            };
            if (avatarStored.url) {
                profileUpdates.avatarUrl = avatarStored.url;
            }
            if (coverStored.url) {
                profileUpdates.coverPhotoUrl = coverStored.url;
            }
            const nextMediaId = avatarStored.mediaId || coverStored.mediaId || user.mediaId;
            if (nextMediaId) {
                profileUpdates.mediaId = nextMediaId;
            }
            if (isConsultant) {
                const certification = consultantCertification.trim();
                if (certification) {
                    profileUpdates.consultantCertification = certification;
                }
            }

            if (guidedMode && onEmailChange && email.trim().toLowerCase() !== user.email.toLowerCase()) {
                const result = await onEmailChange(email.trim());
                await onSave(profileUpdates);
                if (result.accessUrl) {
                    setNewAccessUrl(result.accessUrl);
                    return;
                }
            } else {
                await onSave(profileUpdates);
            }
            onClose();
        } catch (err: unknown) {
            console.error('Error saving details', err);
            setError(err instanceof Error ? err.message : 'Error saving details');
        } finally {
            setIsLoading(false);
        }
    };

    const displayName = [firstName, lastName].filter(Boolean).join(' ').trim() || nickname || user.email;
    const displayHandle = nickname.trim() ? `@${nickname.trim()}` : undefined;
    const displayCertification = isConsultant ? consultantCertification.trim() : undefined;
    const profileBadges = resolveUserProfileBadges(user);

    return (
        <>
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-lg flex flex-col transition-colors overflow-hidden max-h-[90vh]">
                <ProfileHeader
                    avatarUrl={normalizeAvatarUrl(avatarUrl || user.avatarUrl)}
                    coverPhotoUrl={normalizeAvatarUrl(coverPhotoUrl || user.coverPhotoUrl)}
                    avatarAlt={displayName}
                    name={displayName}
                    badges={profileBadges}
                    handle={displayHandle}
                    certification={displayCertification}
                    bio={profileBio}
                    onAvatarClick={() => {
                        setIsEditingCover(false);
                        setIsEditingAvatar((prev) => !prev);
                    }}
                    avatarEditable={isEditingAvatar}
                    onCoverClick={() => {
                        setIsEditingAvatar(false);
                        setIsEditingCover((prev) => !prev);
                    }}
                    coverEditable={isEditingCover}
                    actions={
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex h-9 w-9 items-center justify-center rounded-full bg-white/90 dark:bg-slate-900/90 text-slate-500 dark:text-slate-400 shadow-md hover:text-slate-800 dark:hover:text-slate-200 transition-colors text-xl leading-none"
                            aria-label={t('close')}
                        >
                            &times;
                        </button>
                    }
                />

                <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-4 border-t border-slate-100 dark:border-slate-800 overflow-y-auto">
                    {error && (
                        <div className="bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300 text-sm p-3 rounded-lg border border-red-100 dark:border-red-900/50 font-bold">
                            {error}
                        </div>
                    )}

                    {isEditingCover && (
                        <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                {t('coverPhotoUrl')}
                            </label>
                            <input
                                type="text"
                                inputMode="url"
                                value={coverPhotoUrl}
                                onChange={(e) => setCoverPhotoUrl(e.target.value)}
                                onBlur={() => {
                                    const normalized = normalizeAvatarUrl(coverPhotoUrl);
                                    if (normalized && normalized !== coverPhotoUrl.trim()) {
                                        setCoverPhotoUrl(normalized);
                                    }
                                }}
                                placeholder={t('coverPhotoUrlPlaceholder')}
                                className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-md focus:ring-1 focus:ring-sky-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 dir-ltr"
                                autoFocus
                            />
                            <p className="mt-1 text-[10px] text-slate-400">{t('tapCoverToEdit')}</p>
                        </div>
                    )}

                    {isEditingAvatar && (
                        <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                {t('avatarUrl')}
                            </label>
                            <input
                                type="text"
                                inputMode="url"
                                value={avatarUrl}
                                onChange={(e) => setAvatarUrl(e.target.value)}
                                onBlur={() => {
                                    const normalized = normalizeAvatarUrl(avatarUrl);
                                    if (normalized && normalized !== avatarUrl.trim()) {
                                        setAvatarUrl(normalized);
                                    }
                                }}
                                placeholder={t('avatarUrlPlaceholder')}
                                className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-md focus:ring-1 focus:ring-sky-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 dir-ltr"
                                autoFocus
                            />
                            <p className="mt-1 text-[10px] text-slate-400">{t('tapAvatarToEdit')}</p>
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">{t('firstName')}</label>
                            <input 
                                type="text" 
                                value={firstName} 
                                onChange={(e) => setFirstName(e.target.value)}
                                className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-md focus:ring-1 focus:ring-sky-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">{t('lastName')}</label>
                            <input 
                                type="text" 
                                value={lastName} 
                                onChange={(e) => setLastName(e.target.value)}
                                className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-md focus:ring-1 focus:ring-sky-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">{t('nickname')}</label>
                        <input 
                            type="text" 
                            value={nickname} 
                            onChange={(e) => setNickname(e.target.value)}
                            className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-md focus:ring-1 focus:ring-sky-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">{t('birthday')}</label>
                        <input 
                            type="date" 
                            value={birthday} 
                            onChange={(e) => setBirthday(e.target.value)}
                            className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-md focus:ring-1 focus:ring-sky-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">{t('profileBio')}</label>
                        <textarea
                            value={profileBio}
                            onChange={(e) => setProfileBio(e.target.value)}
                            rows={3}
                            placeholder={t('profileBioPlaceholder')}
                            className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-md focus:ring-1 focus:ring-sky-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 resize-none"
                        />
                    </div>

                    {user.chefId && !guidedMode && (
                        <div className="bg-brand-sage/10 dark:bg-brand-sage/20 border border-brand-sage/20 rounded-xl p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <ChefHatIcon className="w-5 h-5 text-brand-sage" />
                                <span className="text-[10px] font-black uppercase tracking-widest text-brand-sage">{t('verifiedChef')}</span>
                            </div>
                            {linkedChef && (
                                <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
                                    {getChefDisplayName(linkedChef, user.language || 'en')}
                                </p>
                            )}
                            <button
                                type="button"
                                onClick={() => onViewChef?.(user.chefId!)}
                                className="w-full py-2.5 bg-brand-sage text-white font-black rounded-xl text-xs uppercase tracking-widest hover:opacity-90 transition-opacity"
                            >
                                {t('viewChefCard')}
                            </button>
                        </div>
                    )}

                    {isConsultant && (
                        <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                {t('consultantCertification')}
                            </label>
                            <input
                                type="text"
                                value={consultantCertification}
                                onChange={(e) => setConsultantCertification(e.target.value)}
                                placeholder={t('consultantCertificationPlaceholder')}
                                className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-md focus:ring-1 focus:ring-sky-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                            />
                        </div>
                    )}

                    {guidedMode && (
                        <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">{t('email')}</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800 dir-ltr"
                            />
                            {newAccessUrl && (
                                <div className="mt-2 flex items-center gap-2 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                                    <span className="text-xs break-all flex-1 dir-ltr">{newAccessUrl}</span>
                                    <CopyButton text={newAccessUrl} copyLabel={t('copyLink')} copiedLabel={t('copied')} />
                                </div>
                            )}
                        </div>
                    )}

                    {!guidedMode && !user.chefId && onRequestChefRecognition && (
                        <button
                            type="button"
                            onClick={onRequestChefRecognition}
                            className="w-full py-3 border-2 border-dashed border-brand-sage/40 text-brand-sage font-black rounded-xl text-xs uppercase tracking-widest hover:bg-brand-sage/5 transition-colors flex items-center justify-center gap-2"
                        >
                            <ChefHatIcon className="w-4 h-4" />
                            {t('requestChefRecognition')}
                        </button>
                    )}

                    {guidedMode && onGoPro && (
                        <button
                            type="button"
                            onClick={onGoPro}
                            className="w-full py-3 bg-brand-orange text-white font-black rounded-xl text-xs uppercase tracking-widest"
                        >
                            {t('goPro')}
                        </button>
                    )}

                    <div className="flex justify-end pt-4 border-t border-slate-100 dark:border-slate-800 mt-4 gap-2">
                        <button 
                            type="button" 
                            onClick={onClose} 
                            className="px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-md hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                        >
                            {t('cancel')}
                        </button>
                        <button 
                            type="submit" 
                            disabled={isLoading}
                            className="px-4 py-2 bg-sky-600 dark:bg-sky-700 text-white rounded-md hover:bg-sky-700 dark:hover:bg-sky-600 flex items-center gap-2 disabled:opacity-50 transition-colors"
                        >
                            {isLoading ? '...' : t('save')}
                        </button>
                    </div>
                </form>
            </div>
        </div>

        {showReservedNicknameModal && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
                <div
                    className="bg-white dark:bg-slate-900 w-full max-w-sm rounded-2xl shadow-2xl p-6 border dark:border-slate-800 text-center"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="flex justify-center mb-4">
                        <div className="p-3 rounded-full bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400">
                            <ChefHatIcon className="w-8 h-8" />
                        </div>
                    </div>
                    <h3 className="text-lg font-black text-slate-800 dark:text-slate-100 mb-2">{t('nickname')}</h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">{t('nicknameReserved')}</p>
                    <button
                        type="button"
                        onClick={() => setShowReservedNicknameModal(false)}
                        className="w-full py-3 bg-brand-sage text-white font-black rounded-xl text-xs uppercase tracking-widest hover:opacity-90 transition-opacity"
                    >
                        {t('close')}
                    </button>
                </div>
            </div>
        )}
        </>
    );
};

export default PersonalDetailsModal;
